import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import {
  selectSeller,
  getSellerModuleAction,
  selectSellerModules,
  getCreatorInfo,
  directContactSellerAction,
  setForbidAds
} from "../../routes/Ad/ad.ducks";
import { selectCategories } from "../App/selectors";
import { updateFilteredCategories } from "../App/actions";
import { submitFormData } from "../../HOCs";
import { clearAdsSearchResults, searchAds, selectResults } from "../../routes/Search/search.ducks";
import styles from "./styles/AdsSellerPage.scss";
import SellerInfoCard from "../../components/SellerInfoCard";
import { SearchSection } from "../SearchPage/SearchSection";
import HelmetComponent from "../../utils/HelmetHelper";
import { getImageLink } from "../../services/asset";
import { checkEmptyObject, getWindowWidth, isSearchMobile } from "../../services";
import { categoriesInterface, creatorInterface, modulesInterface, searchAdsInterface, UserI } from "../../typings/interfaces";
import { HashTag, LinkTargetEnum } from "../../models";
import { selectFavoriteIds } from "../../routes/Favorite/favorite.ducks";
import { selectLoadedUser } from "../../routes/Auth/auth.ducks";
import { navigateToLogin } from "../../history";
import { Ads } from "../../services/api";
import BreadcrumbBar from "../../components/BreadcrumbBar";

export interface AdsSellerPageProps {
  seller: creatorInterface;
  modules: modulesInterface[];
  loading: boolean;
  categories: categoriesInterface[];
  ads: searchAdsInterface;
  favoriteIds: string[];
  user: UserI | null;
  getSellerModules: (id: string) => void;
  getCreatorInfo: (username: string) => void;
  searchAds: (data: any, callback: Function) => void;
  clearAdsSearchResults: () => void;
  updateFilteredCategories: (categories: categoriesInterface[]) => void;
  onSubmit: (data: any, callback: Function) => void;
  setForbidAds: (forbid: boolean) => void;

  match: {
    params: {
      username: string;
    }
  }
}

function AdsSellerPage(props: AdsSellerPageProps) {
  const {
    seller,
    modules,
    loading,
    categories,
    ads,
    favoriteIds,
    user
  } = props;

  const [adLoading, setAdLoading] = useState(true);
  const [contactVisible, setContactVisible] = useState(false);
  const queryParamsPrev = useRef({});
  const modulePrev = useRef({});

  useEffect(() => {
    const {
      match: {
        params: { username }
      },
      getSellerModules,
      seller
    } = props;

    props.getCreatorInfo(decodeURIComponent(username));
    props.updateFilteredCategories([]);

    if (seller) {
      getSellerModules(seller.id);
      decodeURIComponent(seller.username).toLowerCase().trim() === decodeURIComponent(username).toLowerCase().trim() && filterAds(checkEmptyObject(modulePrev.current) || undefined, queryParamsPrev.current);
    }
  }, [
    JSON.stringify(seller),
    JSON.stringify(modules) != JSON.stringify([]),
    favoriteIds
  ]);

  useEffect(() => {
    return () => {
      props.clearAdsSearchResults();
      // Reset forbidAds when leaving the seller page
      props.setForbidAds(false);
    };
  }, []);

  useEffect(() => {
    // Track seller page view when seller info is loaded
    if (seller && seller.id) {
      Ads.sellerPageView(seller.id).catch(() => {
        // Silently fail - analytics tracking shouldn't block page load
      });
    }
  }, [seller && seller.id]);

  useEffect(() => {
    // Set forbidAds based on whether seller has any ads
    // Only set to true if we have explicitly received empty results (not null/undefined)
    if (ads && ads.data !== undefined) {
      const hasAds = ads.data.length > 0;
      props.setForbidAds(!hasAds);
    }
  }, [ads]);


  const filterAds = (module?: any, queryParams?: any) => {
    const { seller } = props;
    queryParamsPrev.current = queryParams;
    modulePrev.current = module;

    if (seller) {
      props.searchAds(
        {
          module,
          favoriteIds: favoriteIds,
          queryParams: { userId: seller.id, ...queryParams }
        },
        () => {
          setAdLoading(false);
        }
      );
    } else {
      setAdLoading(false);
    }
  };

  const handleContactSeller = async (data: any, callback: any) => {
    const { seller, onSubmit } = props;

    onSubmit({ id: seller.id, data }, callback);
  };

  const isMobile = isSearchMobile(getWindowWidth());

  const handleContactOpen = () => {
    if (user) {
      setContactVisible(true);
    } else {
      navigateToLogin(window.location.href + HashTag.contactAfterLogin);
    }
  };

  const handleContactClose = () => {
    setContactVisible(false);
  };


  if (!seller) {
    return null;
  }

  return (
    <React.Fragment>
      <HelmetComponent
        title={`Korisnik: ${seller.username} | ${"INDEX OGLASI"}`}
        ogTitle={`${"Pogledaj sve oglase"} korisnika: ${seller.username} | ${"INDEX OGLASI"}`}
        ogImage={getImageLink(seller.avatar)}
      />
      <div className={styles.container}>
        <BreadcrumbBar
          routeLinks={[
            {
              title: `${seller.username}`,
              link: null
            }
          ]}
          category=""
          className={styles.breadcrumb}
        />

        {/* Search section with seller card, filters and ads */}
        {isMobile ? (
          <React.Fragment>
            <div className={styles.sellerCardWrapper}>
              <SellerInfoCard
                seller={seller}
                gaLabel="Ocijeni - stranica svih oglasa"
              />
            </div>
            <SearchSection
              onSubmit={filterAds}
              categories={categories}
              // @ts-ignore
              modules={modules}
              ads={ads}
              loading={adLoading}
              isMobile={true}
              target={LinkTargetEnum.blank}
              shouldHilightPromotedAds={true}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className={styles.searchSectionContainer}>
              <SearchSection
                onSubmit={filterAds}
                categories={categories}
                modules={modules}
                ads={ads}
                loading={adLoading}
                target={LinkTargetEnum.blank}
                shouldHilightPromotedAds={true}
                filterHeader={
                  <div className={styles.sellerCardWrapper}>
                    <SellerInfoCard
                      seller={seller}
                      gaLabel="Ocijeni - stranica svih oglasa"
                    />
                  </div>
                }
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
}

AdsSellerPage.defaultProps = {
  ads: null,
  seller: {},
  user: null
};

export default connect(
    state => ({
      seller: selectSeller(state),
      modules: selectSellerModules(state),
      ads: selectResults(state),
      categories: selectCategories(state),
      favoriteIds: selectFavoriteIds(state),
      user: selectLoadedUser(state),
    }),
    {
      getCreatorInfo,
      searchAds,
      clearAdsSearchResults,
      getSellerModules: getSellerModuleAction,
      directContactSeller: directContactSellerAction,
      updateFilteredCategories,
      setForbidAds
    }
  )(submitFormData(AdsSellerPage, directContactSellerAction));



// WEBPACK FOOTER //
// ./src/containers/AdsSellerPage/index.tsx